import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import da from "./locales/da.json";
import { languageForDeviceRegion } from "./locales/regionLanguage";

/**
 * i18n bootstrap — imported once per entry point (src/main.tsx for the app,
 * catalog/main.tsx for the apex marketing bundle). react-i18next uses a single
 * global instance, so importing this module is all a component needs before it
 * can call {@code useTranslation()} / render {@code <Trans>}.
 *
 * <p>Scope of this spike: the externally-visible surface only — the landing
 * page, the sign-in interstitial, and the Privacy + Cookie legal pages. The
 * authenticated app and the LLM/agent copy are intentionally NOT wired yet;
 * those strings stay hardcoded English until a later increment.</p>
 *
 * <p>Language selection, in order: a saved choice from localStorage, then the
 * device's region (see {@link languageForDeviceRegion}), then the browser's
 * own language, then English. A user's pick via the language switcher is
 * written back to localStorage under the {@code i18nextLng} key, so it
 * persists across visits on that browser.</p>
 *
 * <p>The region step sits ahead of {@code navigator} on purpose. Plenty of
 * people in Denmark run an English-language OS, so the browser reports a
 * perfectly valid "en" and Danish would never surface — the region check is
 * the only thing that closes that gap. It stays behind {@code localStorage}
 * so it can only ever affect a visitor who has not chosen for themselves.</p>
 */
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
] as const;

/**
 * Guesses from the device timezone. Returning undefined means "no opinion",
 * which hands the decision to the next detector in the order.
 */
const regionDetector = {
  name: "deviceRegion",
  lookup: () => languageForDeviceRegion(),
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(regionDetector);

void i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      da: { translation: da },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "da"],
    // We only ship base languages; treat "da-DK" etc. as "da".
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    detection: {
      order: ["localStorage", "deviceRegion", "navigator"],
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"],
    },
    interpolation: {
      // React already escapes; double-escaping would mangle punctuation.
      escapeValue: false,
    },
  });

// Keep <html lang> in sync so screen readers and the browser announce the
// right language, and reflect the initial detected value immediately.
const applyHtmlLang = (lng: string) => {
  document.documentElement.lang = lng.split("-")[0];
};
applyHtmlLang(i18n.resolvedLanguage ?? "en");
i18n.on("languageChanged", applyHtmlLang);

export default i18n;
